import React, { useContext, useEffect } from 'react';
import uuid from 'uuid/v4';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  TextButton,
  Button,
  CounterBadge,
  Box,
  Divider,
} from '@wix/design-system';
import {
  InfoCircle,
  Desktop,
  Mobile,
  CommentSmall,
  Comment,
} from '@wix/wix-ui-icons-common';
import { accountLogo, experiments } from '../../fromWindow';
import { ViewModeContext } from '../App/ViewModeContext';
import { ResponsiveSiteContext } from '../App/ResponsiveSiteContext';
import { DocumentServicesContext } from '../App/DocumentServicesContext';
import { ClientContext } from '../App/ClientContext';
import Logo from '../../assets/images/wix-logo.svg';
import EditorXLogo from '../../assets/images/editor-x-logo.svg';
import StudioLogo from '../../assets/images/studio-logo.svg';
import CommentsListOld from '../CommentsList/CommentsListOld';
import CommentsList from '../CommentsList/CommentsList';
import { COMMENT_STATUS, EDITOR_TYPE, VIEW_MODE } from '../../utils/constants';
import Breakpoints from './Breakpoints';
import logger from '../../utils/biLogger';
import {
  getFeedbackChooseMode,
  getFeedbackHideOrShowCommentList,
} from '@wix/bi-logger-get-feedback/v2';
import s from './Header.scss';
import { ACTIONS, useAppContext } from '../App/AppContext';

const unreadCount = comments =>
  comments.filter(c => c.status === COMMENT_STATUS.UNREAD).length;

const Header = ({
  comments,
  onDesktopClick,
  onMobileClick,
  onHelpButtonClick,
  onOpenComment,
  closeAllComments,
  onSetFilterOpenComments,
  onSetFilterResolvedComments,
  resolvedFilterOn,
  openFilterOn,
  isLoadingSite,
}) => {
  const { t } = useTranslation();
  const {
    state: { isPartner, editorType, role, shouldShowCommentsList },
    dispatch,
  } = useAppContext();
  const {
    user: { contactId },
  } = useContext(ClientContext);
  const { viewMode, setViewMode } = useContext(ViewModeContext);
  const { isSiteResponsive } = useContext(ResponsiveSiteContext);
  const { documentServices } = useContext(DocumentServicesContext);

  useEffect(() => {
    if (siteLoadedAndShouldShowCommentsList()) {
      dispatch({ type: ACTIONS.SET_SHOULD_SHOW_COMMENTS_LIST, payload: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingSite]);

  const isSiteEditorTypeStudio = () => editorType === EDITOR_TYPE.STUDIO;

  function siteLoadedAndShouldShowCommentsList() {
    return (
      _.isBoolean(isLoadingSite) && !isLoadingSite && shouldShowCommentsList
    );
  }
  function getTabClassName(type) {
    return type === viewMode ? s.active : '';
  }

  function renderViewMode() {
    return (
      <ul className={s.tabs}>
        <li className={getTabClassName(VIEW_MODE.DESKTOP)}>
          <TextButton
            dataHook="desktop-button"
            skin="dark"
            prefixIcon={<Desktop />}
            onClick={() => {
              logger.report(
                getFeedbackChooseMode({
                  editorVersion: editorType,
                  mode: VIEW_MODE.DESKTOP,
                  ...(contactId
                    ? { _client_id: contactId }
                    : { _client_id: uuid() }),
                }),
              );
              setViewMode(VIEW_MODE.DESKTOP);
              onDesktopClick();
            }}
          >
            {t('get_feedback_header_view_type_button_desktop')}
          </TextButton>
        </li>
        <li className={getTabClassName(VIEW_MODE.MOBILE)}>
          <TextButton
            dataHook="mobile-button"
            skin="dark"
            prefixIcon={<Mobile />}
            onClick={() => {
              logger.report(
                getFeedbackChooseMode({
                  editorVersion: editorType,
                  mode: VIEW_MODE.DESKTOP,
                  ...(contactId
                    ? { _client_id: contactId }
                    : { _client_id: uuid() }),
                }),
              );
              setViewMode(VIEW_MODE.MOBILE);
              onMobileClick();
            }}
          >
            {t('get_feedback_header_view_type_button_mobile')}
          </TextButton>
        </li>
      </ul>
    );
  }

  function renderHeader() {
    if (_.isBoolean(isLoadingSite) && !isLoadingSite) {
      if (isSiteResponsive) {
        const breakpoints = documentServices.breakpoints.get(
          documentServices.pages.getCurrentPage(),
        );
        return <Breakpoints breakpoints={breakpoints} />;
      } else {
        return renderViewMode();
      }
    }
  }

  function renderHelpForOwner() {
    return (
      <Box
        verticalAlign="middle"
        height="100%"
        className={isSiteEditorTypeStudio() ? s.helpBtn : ''}
      >
        <Box marginRight="18px" marginLeft="18px">
          <TextButton
            size={isSiteEditorTypeStudio() ? 'small' : 'medium'}
            weight="thin"
            style={{
              color: isSiteEditorTypeStudio() ? '#FFFFFF' : null,
            }}
            prefixIcon={
              <InfoCircle color={isSiteEditorTypeStudio() ? '#FFFFFF' : null} />
            }
            dataHook="help-button"
            skin="dark"
            onClick={onHelpButtonClick}
          >
            {t('get_feedback_header_help_button')}
          </TextButton>
        </Box>
      </Box>
    );
  }

  function renderPartnerOrWixLogo() {
    if (isPartner && accountLogo) {
      return <img height="30px" alt="logo" src={accountLogo} />;
    }
    if (editorType === EDITOR_TYPE.EDITORX) {
      return <EditorXLogo data-hook="header-editor-x-logo" />;
    } else if (isSiteEditorTypeStudio()) {
      return <StudioLogo data-hook="header-studio-logo" />;
    } else {
      return <Logo data-hook="header-logo" />;
    }
  }

  const renderHeaderCommentsSection = () => {
    return (
      !isSiteEditorTypeStudio() && (
        <div data-hook="owner-panel">
          <span
            style={{ position: 'relative', cursor: 'pointer' }}
            onClick={() => {
              logger.report(
                getFeedbackHideOrShowCommentList({
                  origin: 'view-comment-page',
                  user_type: role,
                  editorVersion: editorType,
                  action: shouldShowCommentsList
                    ? 'hide-comments'
                    : 'show-comments',
                }),
              );
              dispatch({
                type: ACTIONS.SET_SHOULD_SHOW_COMMENTS_LIST,
                payload: !shouldShowCommentsList,
              });
            }}
            data-hook="toggle-comments-button"
          >
            <Box>
              <TextButton
                prefixIcon={<Comment color="#3899EC" />}
                suffixIcon={
                  unreadCount(comments) > 0 && (
                    <Box verticalAlign="middle">
                      <CounterBadge
                        size="small"
                        dataHook="unread-count-badge"
                        skin="danger"
                      >
                        {unreadCount(comments)}
                      </CounterBadge>
                    </Box>
                  )
                }
              >
                {t('get_feedback_studio-header_add_comments_button')}
              </TextButton>
            </Box>
          </span>
        </div>
      )
    );
  };

  const renderCommentsListByExperiment = () => {
    if (experiments['specs.experts.studioFeedbackOwnerApp'] === 'true') {
      return (
        <CommentsList
          comments={comments}
          onSetFilterOpenComments={toggle => onSetFilterOpenComments(toggle)}
          onSetFilterResolvedComments={toggle =>
            onSetFilterResolvedComments(toggle)
          }
          resolvedFilterOn={resolvedFilterOn}
          openFilterOn={openFilterOn}
          isShown={siteLoadedAndShouldShowCommentsList()}
          onOpenComment={comment => {
            onOpenComment(comment);
          }}
        />
      );
    } else {
      return (
        <CommentsListOld
          comments={comments}
          onSetFilterOpenComments={toggle => onSetFilterOpenComments(toggle)}
          onSetFilterResolvedComments={toggle =>
            onSetFilterResolvedComments(toggle)
          }
          resolvedFilterOn={resolvedFilterOn}
          openFilterOn={openFilterOn}
          isShown={siteLoadedAndShouldShowCommentsList()}
          onOpenComment={comment => {
            onOpenComment(comment);
          }}
        />
      );
    }
  };
  const renderOwnerCommentsList = () => {
    return <>{!isLoadingSite ? renderCommentsListByExperiment() : null}</>;
  };

  const renderStudioSitesHeaderOpenCommentsSection = () => {
    return (
      <div style={{ height: '100%' }}>
        <Button
          size="small"
          weight="normal"
          dataHook="add-comment-button"
          onClick={() => {
            logger.report(
              getFeedbackHideOrShowCommentList({
                origin: 'view-comment-page',
                user_type: role,
                editorVersion: editorType,
                action: shouldShowCommentsList
                  ? 'hide-comments'
                  : 'show-comments',
              }),
            );
            dispatch({
              type: ACTIONS.SET_SHOULD_SHOW_COMMENTS_LIST,
              payload: !shouldShowCommentsList,
            });
          }}
          data-hook="toggle-comments-button"
          prefixIcon={<CommentSmall />}
          suffixIcon={
            unreadCount(comments) > 0 ? (
              <Box verticalAlign="middle">
                <Box>
                  <CounterBadge
                    size="small"
                    weight="normal"
                    dataHook="unread-count-badge"
                    skin="light"
                  >
                    {unreadCount(comments)}
                  </CounterBadge>
                </Box>
              </Box>
            ) : null
          }
          className={s.commentsButton}
        >
          {t('get_feedback_studio-header_add_comments_button')}
        </Button>
        {renderOwnerCommentsList()}
      </div>
    );
  };

  return (
    <div
      className={`${s.root} ${
        isSiteEditorTypeStudio() ? s.rootStudioBGColor : s.rootBGColor
      }`}
      onClick={() => {
        closeAllComments();
      }}
    >
      <div className={s.logoSection}>{renderPartnerOrWixLogo()}</div>
      <div className={s.viewType}>{renderHeader()}</div>
      <div className={s.right}>
        {renderHelpForOwner()}
        {!isSiteEditorTypeStudio() && (
          <Divider direction="vertical" className={s.divider} />
        )}
        <>
          <>{renderHeaderCommentsSection()}</>
          <>{renderOwnerCommentsList()}</>
        </>
      </div>
      {isSiteEditorTypeStudio() && isSiteEditorTypeStudio()
        ? renderStudioSitesHeaderOpenCommentsSection()
        : null}
    </div>
  );
};

export default Header;
